import { AbiQuery } from './abi/query.js';
import { validateAbi } from './abi/validations.js';
import { base16 } from './support/base.js';
import { hash } from './support/blake3.js';
import { BCS, BufReader, BufWriter, Lock, LockSerializer, Pointer, PointerSerializer } from './internal.js';
/**
 * Transaction Execution Output
 *
 * When a transaction is executed it creates one or more outputs. An output
 * has the following fields:
 *
 * - `origin`: a 36-byte pointer to the transaction the instance was first instantiated
 * - `location`: a 36-byte pointer to the transaction of the current state of the instance
 * - `class`: a 36-byte pointer to the package the class is found in
 * - `lock`: the Lock type and data
 * - `state`: BCS encoded output state
 *
 * The Output class is a generic wrapper for any output, and provides a way to
 * access the parsed state as a JavaScript object.
 */
export class Output {
    origin;
    location;
    classPtr;
    lock;
    stateBuf;
    #abi;
    #bcs;
    #classNode;
    #props;
    constructor(origin, location, classPtr, lock, stateBuf, abi) {
        this.origin = origin;
        this.location = location;
        this.classPtr = classPtr;
        this.lock = lock;
        this.stateBuf = stateBuf;
        if (typeof abi !== 'undefined') {
            this.abi = abi;
        }
    }
    static fromBytes(bytes, abi) {
        if (!ArrayBuffer.isView(bytes)) {
            throw Error('The first argument to `Output.fromBytes()` must be a `Uint8Array`');
        }
        const buf = new BufReader(bytes);
        const output = buf.read(OutputSerializer);
        if (typeof abi !== 'undefined') {
            output.abi = abi;
        }
        return output;
    }
    static fromHex(hex, abi) {
        let buff = base16.decode(hex);
        return this.fromBytes(buff, abi);
    }
    static fromJson(data, abi) {
        const output = new Output(Pointer.fromString(data.origin), Pointer.fromString(data.location), Pointer.fromString(data.class), Lock.fromJson(data.lock), base16.decode(data.state));
        if (data.id !== output.id) {
            throw Error('invalid id. the given id does not match the computed id.');
        }
        if (typeof abi !== 'undefined') {
            output.abi = abi;
        }
        return output;
    }
    set abi(abi) {
        if (typeof abi === 'undefined') {
            this.#abi = undefined;
            this.#bcs = undefined;
            this.#classNode = undefined;
        }
        else if (validateAbi(abi)) {
            this.#abi = abi;
            this.#bcs = new BCS(abi);
            this.#classNode = new AbiQuery(abi).fromExports().byIndex(this.classPtr.idx).getClass();
        }
    }
    get abi() {
        return this.#abi;
    }
    get id() {
        return base16.encode(this.hash);
    }
    get hash() {
        return hash(this.toBytes());
    }
    get className() {
        if (this.#classNode) {
            return this.#classNode.name;
        }
    }
    get classNode() {
        return this.#classNode;
    }
    get props() {
        if (this.#bcs && this.#classNode && typeof this.#props === 'undefined') {
            const state = this.#bcs.decode(this.#classNode.name, this.stateBuf);
            const props = this.#classNode.fields.reduce((props, f, i) => {
                props[f.name] = state[i];
                return props;
            }, {});
            this.#props = Object.freeze(props);
        }
        return this.#props;
    }
    toBytes() {
        const buf = new BufWriter();
        buf.write(OutputSerializer, this);
        return buf.data;
    }
    toHex() {
        return base16.encode(this.toBytes());
    }
    toJson() {
        return {
            id: this.id,
            origin: this.origin.toString(),
            location: this.location.toString(),
            class: this.classPtr.toString(),
            lock: this.lock.toJson(),
            state: base16.encode(this.stateBuf)
        };
    }
}
/**
 * Output Serializer object - implements the Serializable interface.
 */
export const OutputSerializer = {
    read(buf) {
        const origin = buf.read(PointerSerializer);
        const location = buf.read(PointerSerializer);
        const classPtr = buf.read(PointerSerializer);
        const lock = buf.read(LockSerializer);
        const stateBuf = buf.readBytes();
        return new Output(origin, location, classPtr, lock, stateBuf);
    },
    write(buf, output) {
        buf.write(PointerSerializer, output.origin);
        buf.write(PointerSerializer, output.location);
        buf.write(PointerSerializer, output.classPtr);
        buf.write(LockSerializer, output.lock);
        buf.writeBytes(output.stateBuf);
        return buf;
    }
};
